import {faCogs} from "@fortawesome/pro-light-svg-icons/faCogs";
import {faBomb} from "@fortawesome/pro-solid-svg-icons/faBomb";
import {faClipboard} from "@fortawesome/pro-solid-svg-icons/faClipboard";
import {faCopy} from "@fortawesome/pro-solid-svg-icons/faCopy";
import {faExternalLink} from "@fortawesome/pro-solid-svg-icons/faExternalLink";
import {faPage} from "@fortawesome/pro-solid-svg-icons/faPage";
import {faPaperPlane} from "@fortawesome/pro-solid-svg-icons/faPaperPlane";
import {faRectangleHistory} from "@fortawesome/pro-solid-svg-icons/faRectangleHistory";
import {faTrash} from "@fortawesome/pro-solid-svg-icons/faTrash";
import {faUsers} from "@fortawesome/pro-solid-svg-icons/faUsers";
import {faWavePulse} from "@fortawesome/pro-solid-svg-icons/faWavePulse";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    GetItemCounts,
    SendsPageItemType,
    updateSendsPageActiveStatus,
    useChangeSendsStatus,
    useInfiniteSendsPage
} from "@twigsby/client-data";
import {
    PageHeader,
    Button,
    ButtonSize,
    ColourSolid,
    DropdownMenu,
    FlexAlign,
    FlexGap,
    FlexJustify,
    FullWidthSidebar,
    InfiniteLoadTrigger,
    ItemMenuEntry,
    ItemRow,
    ItemRowHeader,
    PillSquare,
    StackHorizontal,
    StackVertical,
    ToggleSwitch,
    ToggleType, Tooltip,
    useLayout,
    useNotifications,
    useTranslation, RichEmptyState
} from "@twigsby/frontend-framework";
import {GetInitials, ParseURL} from "@twigsby/helpers";
import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {EndpointError} from "../../Components/EndpointError";
import ConfirmDeleteSendsPageModal from "../../Components/SendsAdmin/Modals/ConfirmDeleteSendsPageModal";
import SendsNavigation from "../../Components/SendMail/Shared/Controls/SendsNavigation";
import {useSite} from "../../Providers/SiteInterfaceProvider";

const Profiles = () => {
    const { t } = useTranslation();
    const { currentLocale, updatePageMeta } = useSite();
    const navigate = useNavigate();

    const { isMedium, isLarge, isXL } = useLayout();
    const { addNotification } = useNotifications();

    const {
        status,
        data,
        error,
        isSuccess,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage
    } = useInfiniteSendsPage();

    const [ activeSendsPage, setActiveSendsPage ] = useState<any>({ isOpen: false, pageData: null});
    const [ itemCounts, setItemCounts ] = useState<any>({ loaded: 0, total: 0 } );

    const updateSendsStatus = useChangeSendsStatus();

    const sendsPagesRef = useRef<any>();
    const sendMailNavigationRef = useRef<any>();

    const HandleTemplateStatus = async (pageId: number, active: any) => {
        await updateSendsPageActiveStatus(pageId, active).then((resp) => {
            updateSendsStatus.mutate({
                newData: active,
                id: pageId,
            })
        })
    }

    const ShowDeleteConfirmation = (page: SendsPageItemType) => {
        setActiveSendsPage({ pageData: page, isOpen: true })
    }

    const HandleOpen = async (slug: string) => {
        window.open(ParseURL(slug), '_blank');
    }

    const HandleCopyUrl = async (slug: string) => {
        addNotification({
            title: t('results.copiedClipboard'),
            icon: faClipboard
        });
        await navigator.clipboard.writeText(ParseURL(slug));
    }

    const ItemPlaceholder = () => {
        return (
            <div className={'content-loader content-fader'} style={{ display: 'grid', gap: 'var(--sm-unit)', margin: '0 var(--md-unit)', gridTemplateColumns: isMedium ? 'repeat(3, 1fr) 5em' : '1fr 5em'}}>
                {isMedium ? (
                    <>
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                        <div style={{ width: 'auto', height: '3em', marginBlock: 'var(--lg-unit)' }} />
                    </>
                ) : (
                    <>
                        <div style={{ width: 'auto', height: '2em' }} />
                        <div style={{ width: 'auto', height: '2em' }} />
                    </>
                )}
            </div>
        )
    }

    const PageActions = () => {
        return (
            <Button
                colour={ColourSolid.Primary}
                onClick={() => navigate('/send-mail/create')}
                squared={true}
                title={t('sends.profiles.actions.new')}
                icon={faPage}
            />
        )
    }

    useEffect(() => {
        if(data) {
            setItemCounts(GetItemCounts(data as any));
        }
    }, [data]);

    useEffect(() => {
        updatePageMeta({
            title: t('sends.profiles.title')
        });
    }, [currentLocale]);

    return  (
        <FullWidthSidebar
            ref={sendsPagesRef}
            navigationComponent={
                <>
                    <SendsNavigation
                        ref={sendMailNavigationRef}
                        title={t('sends.profiles.title')}
                        addonContent={
                            <div className={'section-navigation-header-collapsed-addon'}>
                                {isSuccess && !isLarge && (
                                    <PageActions />
                                )}
                            </div>
                        }
                    />
                </>
            }
            extraContentClass={`flex-column sends-templates-rows`}
        >
            {isLarge && (
                <PageHeader title={t('sends.profiles.title')}>
                    {isSuccess && (
                        <PageActions />
                    )}
                </PageHeader>
            )}

            {isMedium && (
                <ItemRowHeader sticky={true}>
                    <div><span className={'pl-lg'}>{t('sends.profiles.headings.name')}</span></div>
                    <div>{t('sends.profiles.headings.users')}</div>
                    <div>{t('sends.profiles.headings.actions')}</div>
                    <div>{t('sends.profiles.headings.active')}</div>
                </ItemRowHeader>
            )}

            {status === "loading" ? (
                <div className={'content-fader'}>
                    <ItemPlaceholder/>
                    <ItemPlaceholder/>
                    <ItemPlaceholder/>
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                    <ItemPlaceholder />
                </div>
            ) : status === "error" ? (
                <EndpointError {...error} stacked={true} extraClass={'m-md'} />
            ) : (
                <>
                    {data && data.pages.map((group) =>
                        group.data.map((pageEntry, index) => {
                            return (
                                <ItemRow
                                    key={pageEntry.id}
                                    actionLink={{ url: `/send-mail/edit/${pageEntry.id}`}}
                                >
                                    {isMedium ? (
                                        <>
                                            <div className={'flex-row align-center flex-gap-xs pl-lg'}>
                                                <div className={'flex-column flex-gap-xs'}>
                                                    {pageEntry.title}
                                                </div>
                                            </div>
                                            <StackHorizontal gap={FlexGap.ExtraSmall}>
                                                {pageEntry.users.map((user, index) => {
                                                    if(pageEntry.users.length <= 3 && isXL) {
                                                        return (
                                                            <PillSquare key={`pageUser${pageEntry.id}${index}`} content={user.name} colour={ColourSolid.Secondary} />
                                                        )
                                                    }

                                                    return (
                                                        <Tooltip content={user.name}>
                                                            <PillSquare key={`pageUser${pageEntry.id}${index}`} content={GetInitials(user.name as string)} colour={ColourSolid.Secondary} />
                                                        </Tooltip>
                                                    )
                                                })}
                                            </StackHorizontal>
                                            <StackHorizontal gap={FlexGap.ExtraSmall}>
                                                <Button onClick={(e) => [e.preventDefault(), HandleOpen(pageEntry.liveUrl ? pageEntry.liveUrl : '')]} size={ButtonSize.Small} colour={ColourSolid.Secondary} title={t('sends.profiles.actions.open')} iconOnly={!isXL} icon={faExternalLink} squared={true} />
                                                <Button onClick={(e) => [e.preventDefault(), HandleCopyUrl(pageEntry.liveUrl ? pageEntry.liveUrl : '')]} size={ButtonSize.Small} colour={ColourSolid.Secondary} title={t('sends.profiles.actions.copyUrl')} iconOnly={!isXL} icon={faCopy} squared={true} />
                                                <Button onClick={(e) => [e.preventDefault(), ShowDeleteConfirmation(pageEntry)]} size={ButtonSize.Small} colour={ColourSolid.Alert} title={t('sends.profiles.actions.delete')} iconOnly={!isXL} icon={faTrash} squared={true} />
                                            </StackHorizontal>
                                            <div onClick={(e) => e.stopPropagation()}>
                                                <ToggleSwitch type={ToggleType.Switch} hideLabel={true} name={`sendsPageEntry${pageEntry.id}`} label={t('sends.profiles.heading.active')} defaultChecked={pageEntry.isActive} onChange={(e: any) => HandleTemplateStatus(pageEntry.id ? pageEntry.id : -1, e.currentTarget.checked)} />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <StackVertical>
                                                <div className={'flex-row align-center flex-gap-xs'}>
                                                    <div className={'flex-column flex-gap-xs'}>
                                                        <h5 className={'m-0'}>
                                                            {pageEntry.title}
                                                        </h5>
                                                    </div>
                                                </div>
                                                <StackHorizontal gap={FlexGap.ExtraSmall}>
                                                    {pageEntry.users.map((user, index) => {
                                                        if(pageEntry.users.length <= 3 && isXL) {
                                                            return (
                                                                <PillSquare key={`pageUser${pageEntry.id}${index}`} content={user.name} colour={ColourSolid.Secondary} />
                                                            )
                                                        }

                                                        return (
                                                            <Tooltip content={user.name}>
                                                                <PillSquare key={`pageUser${pageEntry.id}${index}`} content={GetInitials(user.name as string)} colour={ColourSolid.Secondary} />
                                                            </Tooltip>
                                                        )
                                                    })}
                                                </StackHorizontal>
                                            </StackVertical>

                                            <div onClick={(e) => e.preventDefault()}>
                                                <DropdownMenu
                                                    trigger={
                                                        <Button title={t('actions.title')} squared={true} icon={faCogs} iconOnly={true} onClick={() => null}/>
                                                    }
                                                >
                                                    <ItemMenuEntry
                                                        title={t('sends.profilesHistory.title')}
                                                        onClick={() => navigate(`/send-mail/history`, { state: { pageIds: pageEntry.id } })}
                                                        icon={faRectangleHistory}
                                                    />

                                                    <ItemMenuEntry
                                                        title={t("divider")}
                                                        sectionDivider={true}
                                                    />

                                                    <ItemMenuEntry
                                                        title={t('sends.profiles.actions.open')}
                                                        onClick={() => HandleOpen(pageEntry.liveUrl ? pageEntry.liveUrl : '')}
                                                        icon={faExternalLink}
                                                    />

                                                    <ItemMenuEntry
                                                        title={t('sends.profiles.actions.copyUrl')}
                                                        onClick={() => HandleCopyUrl(pageEntry.liveUrl ? pageEntry.liveUrl : '')}
                                                        icon={faCopy}
                                                    />

                                                    <ItemMenuEntry title={t("divider")} sectionDivider={true} />

                                                    <ItemMenuEntry
                                                        title={t('sends.profiles.actions.active')}
                                                        icon={faWavePulse}
                                                        onClick={() => HandleTemplateStatus(pageEntry.id ? pageEntry.id : -1, !pageEntry.isActive)}
                                                        addonContent={
                                                            <ToggleSwitch type={ToggleType.Switch} hideLabel={true} label={t('sends.profiles.actions.active')} name={`isActive${pageEntry.id}`} defaultChecked={pageEntry.isActive} />
                                                        }
                                                    />

                                                    <ItemMenuEntry
                                                        title={t('sends.profiles.actions.delete')}
                                                        onClick={() => ShowDeleteConfirmation(pageEntry)}
                                                        icon={faTrash}
                                                    />

                                                </DropdownMenu>
                                            </div>
                                        </>
                                    )}
                                </ItemRow>
                            )}
                        )
                    )}

                    {itemCounts.total === 0 && (
                        <RichEmptyState
                            title={t("sends.profiles.noData.title")}
                            description={t("sends.profiles.noData.description")}
                            media={<FontAwesomeIcon icon={faPaperPlane} size={"3x"} />}
                            addon={<Button title={t("sends.profiles.actions.getStarted")} colour={ColourSolid.Primary} onClick={() => navigate('/send-mail/create')} />}
                        />
                    )}

                    {itemCounts.total > 0 && (
                        <InfiniteLoadTrigger
                            loaded={itemCounts.loaded}
                            totalCount={itemCounts.total}
                            hasNextPage={hasNextPage}
                            fetchNextPage={fetchNextPage}
                            isFetchingNextPage={isFetchingNextPage}
                        />
                    )}
                </>
            )}

            <ConfirmDeleteSendsPageModal
                pageData={activeSendsPage.pageData}
                isOpen={activeSendsPage.isOpen}
                closeCallback={() => setActiveSendsPage({...activeSendsPage, isOpen: false})}
            />

        </FullWidthSidebar>
    )
}

export default Profiles
